import React, { createContext, useState, useEffect, useRef, useContext } from "react";
import jwt_decode from "jwt-decode";
import { useNavigate, useLocation } from "react-router-dom";
import axiosToken from "./axiosToken";
import { refreshToken } from "../api/ApiController";
import { UtilProvider } from "./UtilContext";
import { LocaleProvider, LocaleContext, applyAuthenticatedUserLocalePreferences, clearLanguagePreferencesOnLogout } from "./LocaleContext";
import { PassLogoutProvider } from "../auth/passLogoutRef";
import { uiT } from "../utils/uiTranslate";

const AuthContext = createContext();
export { PassLogoutProvider };

function AuthLoadingScreen() {
  const { t } = useContext(LocaleContext);
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <span className="text-gray-500">{t("Auth.LoadingSession")}</span>
    </div>
  );
}

export const AuthContextProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const pendingTokenRef = useRef(null);
  const [loading, setLoading] = useState(() => !!localStorage.getItem("tokens"));

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!localStorage.getItem("tokens")) {
      setLoading(false);
      return;
    }
    refreshToken().finally(() => setLoading(false));
  }, []);

  const login = async (payload) => {
    return await axiosToken.post(`/api/auth/login`, payload).then((response) => {
      UtilProvider.toast?.current?.clear();

      if (response?.data?.status >= 400) {
        if (response.data.message === "User not found") {
          UtilProvider.setToastPosition("top-center");
          UtilProvider.toast?.current?.show({
            severity: "error",
            summary: uiT("Prihlasenie"),
            detail: uiT("Auth.Error.UserNotFound"),
            sticky: true,
          });
        }
        if (response.data.message === "Bad password") {
          UtilProvider.setToastPosition("top-center");
          UtilProvider.toast?.current?.show({
            severity: "error",
            summary: uiT("Prihlasenie"),
            detail: uiT("Auth.Error.BadPassword"),
            sticky: true,
          });
        }
        return null;
      } else {
        UtilProvider.setToastPosition("top-right");
        const nextToken = JSON.stringify(response.data);
        pendingTokenRef.current = nextToken;
        setToken(nextToken);
        const decoded_token = jwt_decode(response.data.access_token);
        return decoded_token.user;
      }
    }).catch(() => {
      return null;
    });
  };

  const asyncLocalStorage = {
    setItem(key, value) {
      return Promise.resolve().then(function () {
        localStorage.setItem(key, value);
      });
    },
    removeItem(key) {
      return Promise.resolve().then(function () {
        localStorage.removeItem(key);
      });
    },
  };

  const verify = (logged_user) => {
    if (!logged_user) return;
    const nextToken = pendingTokenRef.current || token;
    if (!nextToken) return;
    asyncLocalStorage.setItem("tokens", nextToken).then(function () {
      applyAuthenticatedUserLocalePreferences(logged_user);
      if (location) navigate(`/openTab/${location.search}`);
      else navigate("/");
      setUser(logged_user);
    });
  };

  const logout = async () => {
    pendingTokenRef.current = null;
    setToken(null);
    clearLanguagePreferencesOnLogout();
    try {
      await asyncLocalStorage.removeItem("tokens");
    } catch (e) { /* logout aj pri chybe vymazania tokenov */ }
    setUser(null);
    navigate("/login");
  };

  PassLogoutProvider.logout = logout;
  PassLogoutProvider.user = user;
  PassLogoutProvider.setUser = setUser;

  const providerValue = { user, login, verify, logout, setUser };

  if (loading) {
    return (
      <AuthContext.Provider value={providerValue}>
        <LocaleProvider authUser={null}>
          <AuthLoadingScreen />
        </LocaleProvider>
      </AuthContext.Provider>
    );
  }
  return (
    <AuthContext.Provider value={providerValue}>
      <LocaleProvider authUser={user}>
        {children}
      </LocaleProvider>
    </AuthContext.Provider>
  );
};

export default AuthContext;
